// FE-PINV-064 — TanStack Query wrapper for BE-PINV-062.
//
// One call per facet so the BE's "exclude_facet" parameter applies
// correctly per-bucket. Callers typically issue a parallel set of
// queries (one per facet they want counts for) via
// ``useFilterCountsForFacets``.

import {
  useQueries,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";

import { fetchFilterCounts, type FilterCountsResponse } from "./api";
import type { PipelineFacetKey, PipelineFilters } from "./filterState";


export const FILTER_COUNTS_QUERY_KEY = "pipeline-filter-counts" as const;


export function useFilterCounts(
  filters: PipelineFilters,
  options: { excludeFacet: PipelineFacetKey | null; showClosed: boolean },
): UseQueryResult<FilterCountsResponse> {
  return useQuery({
    queryKey: [
      FILTER_COUNTS_QUERY_KEY, filters,
      options.excludeFacet, options.showClosed,
    ],
    queryFn: ({ signal }) => fetchFilterCounts(filters, {
      excludeFacet: options.excludeFacet,
      showClosed: options.showClosed,
      signal,
    }),
  });
}


export function useFilterCountsForFacets(
  filters: PipelineFilters,
  facets: readonly PipelineFacetKey[],
  showClosed: boolean,
): UseQueryResult<FilterCountsResponse>[] {
  return useQueries({
    queries: facets.map((facet) => ({
      queryKey: [
        FILTER_COUNTS_QUERY_KEY, filters, facet, showClosed,
      ],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        fetchFilterCounts(filters, {
          excludeFacet: facet,
          showClosed,
          signal,
        }),
    })),
  });
}
